import { type ContextFlag, type FeatureType, Language, type RoleResource } from "@newgenerated/shared/schema";
import { getTranslationFunction, type TranslationFn } from "@utils/vue-migration/translationUtils";
import { createHasAccessFunction, setupRoleResourceConstraints } from "@utils/roleService";
import { assert } from "@utils/assertion";
import { getContextFlags, getFeatures } from "@newgenerated/shared/stubs";

export type HasFeatureFn = (feature: FeatureType) => boolean;
export type HasAccessFn = (resource: RoleResource, language?: Language, corporateId?: number) => boolean;
export type GetTranslationFunctionFn = (lang: Language) => TranslationFn;
export type ContextFlagFn = (flag: ContextFlag) => boolean;

type ContextEntry = [key: string, value: unknown];

export async function createContext(options: string[], currentLanguage: Language): Promise<Record<string, unknown>> {
  async function setupHasAccessFn(): Promise<ContextEntry> {
    const constraints = await setupRoleResourceConstraints();
    return ["hasAccess", createHasAccessFunction(constraints)];
  }

  async function setupHasFeatureFn(): Promise<ContextEntry> {
    const features = await getFeatures();
    return ["hasFeature", (feature: FeatureType) => features.kind === "success" && features.data.items.includes(feature)];
  }

  async function setupAllTranslationFn(): Promise<ContextEntry> {
    const translationFns = new Map(
      await Promise.all(
        Language.getValues().map(async (lang): Promise<[Language, TranslationFn]> => {
          return [lang, await getTranslationFunction(lang)];
        }),
      ),
    );

    return [
      "getTranslationFunction",
      (lang: Language): TranslationFn => {
        const translationFn = translationFns.get(lang);
        assert(translationFn !== undefined, `Translation function for language '${lang}' not found`);
        return translationFn;
      },
    ];
  }

  async function setupTranslationFn(lang: Language): Promise<ContextEntry> {
    const t = await getTranslationFunction(lang);
    return ["t", t];
  }

  async function setupFlagsFn(): Promise<ContextEntry> {
    const flags = await getContextFlags();
    return ["contextFlag", (flag: ContextFlag) => flags.kind === "success" && flags.data.items.includes(flag)];
  }

  const promises: Promise<ContextEntry>[] = [];
  if (options.includes("HAS_ACCESS")) {
    promises.push(setupHasAccessFn());
  }

  if (options.includes("FEATURES")) {
    promises.push(setupHasFeatureFn());
  }

  if (options.includes("ALL_LANGUAGES")) {
    promises.push(setupAllTranslationFn());
  }

  if (options.includes("FLAGS")) {
    promises.push(setupFlagsFn());
  }

  promises.push(setupTranslationFn(currentLanguage));

  const contextEntries = await Promise.all(promises);

  contextEntries.push(["currentLanguage", currentLanguage]);

  return Object.fromEntries(new Map(contextEntries).entries());
}
